import { useLayoutEffect, useRef, useState, type ChangeEvent, type InputHTMLAttributes } from 'react';

import { AUTOFILL_IGNORE_PROPS } from '../../../_shared/autofill';
import { displayNumberText, editNumberText } from '../../flow/number-input';

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'inputMode' | 'value' | 'onChange'> & {
  /** The draft text (grouped or not; it is always displayed grouped). */
  value: string;
  /** Receives the grouped text; parse it with `parseNumberInput` (commas are ignored). */
  onChange: (text: string) => void;
};

/**
 * The one numeric text input for the calculator: shows en-US thousands separators at rest and while
 * typing, keeps the caret on the same digit as commas appear or vanish, rejects non-numeric
 * characters, and formats numeric placeholders. It is type="text" because type="number" cannot
 * show commas. The store keeps plain numbers; only the displayed text is grouped.
 */
export function NumberField({ value, onChange, placeholder, ...rest }: Props) {
  const ref = useRef<HTMLInputElement>(null);
  const caret = useRef<number | null>(null);
  const [, bump] = useState(0);
  const shown = displayNumberText(value);

  // After React writes the new value, put the caret back; runs even when the text did not change
  // (a rejected letter), because `bump` forces the render that restores the DOM value.
  useLayoutEffect(() => {
    const el = ref.current;
    if (el && caret.current !== null && document.activeElement === el) el.setSelectionRange(caret.current, caret.current);
    caret.current = null;
  });

  const handle = (e: ChangeEvent<HTMLInputElement>) => {
    const el = e.currentTarget;
    const inputType = (e.nativeEvent as InputEvent).inputType;
    const next = editNumberText(shown, el.value, el.selectionStart ?? el.value.length, inputType);
    caret.current = next.caret;
    onChange(next.text);
    bump((n) => n + 1);
  };

  return (
    <input
      {...AUTOFILL_IGNORE_PROPS}
      {...rest}
      ref={ref}
      type="text"
      inputMode="decimal"
      value={shown}
      placeholder={placeholder === undefined ? undefined : displayNumberText(placeholder)}
      onChange={handle}
    />
  );
}
